import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  Button,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Stack, styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../../../../assets/spero_logo_3.png"; // Update path if needed
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { API_URL } from "../../../Config/api";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 750,
  bgcolor: "background.paper",
  borderRadius: "5px",
  boxShadow: "0px 3px 5px 2px rgba(0, 0, 0, 0.2)",
  pt: 2,
  pb: 3,
  maxHeight: "100vh",
  overflowY: "auto",
};

const PackageInvoice = ({ eveID, onClose }) => {
  const accessToken = localStorage.getItem("token");

  const [invoice, setInvoice] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const sendProformaMail = async () => {
    try {
      setLoading(true);

      const emailResponse = await fetch(
        `${API_URL}/packages/SendProformaEmail/${eveID}/`,
        {
          method: "GET", // Change to "POST" if backend requires it
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      const emailData = await emailResponse.json();
      if (!emailResponse.ok) {
        throw new Error(
          emailData.detail || emailData.message || "Failed to send mail!",
        );
      }

      setSnackbar({
        open: true,
        message: "Proforma email sent successfully!",
        severity: "success",
      });
      setDisabled(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Something went wrong!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    fetch(`${API_URL}/packages/generate_invoice_pdf/${eveID}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement("a");
        const filename = `Package_Invoice_${eveID}.pdf`;
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      })
      .catch((error) => console.error("Error generating PDF:", error));
  };

  useEffect(() => {
    const getInvoice = async () => {
      if (eveID) {
        try {
          const res = await fetch(
            `${API_URL}/packages/invoice_details/?event_id=${eveID}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          );
          const data = await res.json();
          setInvoice(data.Event_Invoice || []);
        } catch (error) {
          console.error("Error fetching Package Invoice:", error);
        }
      }
    };
    getInvoice();
  }, [eveID, accessToken]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
  };

  return (
    <>
      <Box sx={style}>
        <div id="pdfContent" style={{ padding: "2em", fontWeight: "300" }}>
          <div style={{ display: "flex" }}>
            <Typography
              align="center"
              style={{
                fontSize: "25px",
                fontWeight: 900,
                marginTop: "10px",
                marginLeft: "2px",
              }}
            >
              {invoice && invoice.length > 0 && invoice[0]?.amount_paid === 0
                ? "PROFORMA INVOICE"
                : "INVOICE"}
            </Typography>
            <img
              src={logo}
              alt="Logo"
              style={{
                height: "80px",
                width: "125px",
                marginLeft: invoice[0]?.amount_paid === 0 ? "15rem" : "25rem",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex" }}>
                <Typography align="center" variant="subtitle2">
                  HHC No
                </Typography>
                <Typography align="center" variant="body2" sx={{ ml: 1 }}>
                  HHC-{invoice[0]?.patient_hhc_no}
                </Typography>
              </div>
              <div style={{ display: "flex" }}>
                <Typography align="center" variant="subtitle2">
                  Event ID
                </Typography>
                <Typography align="center" variant="body2" sx={{ ml: 1 }}>
                  {invoice[0]?.eve_id}
                </Typography>
              </div>
              <div style={{ display: "flex" }}>
                <Typography align="center" variant="subtitle2">
                  Event Date
                </Typography>
                <Typography align="center" variant="body2" sx={{ ml: 1 }}>
                  {invoice[0]?.event_date}
                </Typography>
              </div>
              <div style={{ display: "flex" }}>
                <Typography align="center" variant="subtitle2">
                  Last Updated
                </Typography>
                <Typography align="center" variant="body2" sx={{ ml: 1 }}>
                  {formatDate(invoice[0]?.last_modified_date)}
                </Typography>
              </div>
            </div>
            <div>
              <Typography
                align="center"
                variant="subtitle2"
                sx={{ ml: 18 }}
                style={{ fontSize: "15px", fontWeight: 600 }}
              >
                BILL DETAILS
              </Typography>
              <div style={{ display: "flex" }}>
                <Typography align="center" variant="subtitle2" sx={{ ml: 2 }}>
                  Invoice ID
                </Typography>
                <Typography align="center" variant="body2" sx={{ ml: 1 }}>
                  {invoice[0]?.hospital_code || "None"}/
                  {invoice[0]?.years_range || "None"}/
                  {invoice[0]?.invoice_id || "0000"}
                </Typography>
              </div>
              <div style={{ display: "flex" }}>
                <Typography align="center" variant="subtitle2" sx={{ ml: 16 }}>
                  Date:
                </Typography>
                <Typography align="center" variant="body2" sx={{ ml: 1 }}>
                  {invoice[0]?.event_date}
                </Typography>
              </div>
            </div>
          </div>
          <hr />

          <div>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }} gutterBottom>
              PATIENT DETAILS
            </Typography>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ display: "flex", marginTop: "10px" }}>
                  <Typography variant="subtitle2">Name:</Typography>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {invoice[0]?.patient_name}
                  </Typography>
                </div>
                <div style={{ display: "flex", marginTop: "10px" }}>
                  <Typography variant="subtitle2">Mobile:</Typography>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {invoice[0]?.patient_number}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" sx={{ mt: 1.2 }}>
                    <b>Residential Address:</b>{" "}
                    {invoice[0]?.patient_google_address}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    <b>Permanent Address:</b> {invoice[0]?.patient_address}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          <hr />

          <div>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}
              gutterBottom
            >
              PACKAGE SERVICE DETAILS
            </Typography>

            {invoice[0]?.services?.map((service, sIndex) => (
              <Box key={sIndex} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    background: "#F8F9FA",
                    p: 1.5,
                    borderRadius: "6px",
                    mb: 2,
                    borderLeft: "4px solid #15CEC9",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Service:{" "}
                    <span style={{ fontWeight: 400 }}>
                      {service.service_name}
                    </span>
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mt: 0.5 }}
                  >
                    Sub Service:{" "}
                    <span style={{ fontWeight: 400 }}>
                      {service.sub_service_name}
                    </span>
                  </Typography>
                </Box>

                <TableContainer
                  component={Box}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#FAAF30" }}>
                        <TableCell sx={{ color: "#FFF", fontWeight: 600 }}>
                          Professional Name
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#FFF", fontWeight: 600 }}
                        >
                          Sessions
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#FFF", fontWeight: 600 }}
                        >
                          Start Date
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "#FFF", fontWeight: 600 }}
                        >
                          End Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {service.professional_amount?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="textSecondary">
                              No Data Available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        service.professional_amount?.map(
                          (professional, profIndex) => (
                            <TableRow
                              key={profIndex}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                "&:hover": { backgroundColor: "#F9F9F9" },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2">
                                  {professional.professional_name}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {professional.sessions}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {professional.start_date}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {professional.end_date}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ),
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}

            {invoice[0]?.conveniance_charges ? (
              <>
                <hr />
                <div
                  style={{
                    display: "flex",
                    marginLeft: "16px",
                    marginTop: "10px",
                  }}
                >
                  <Typography variant="subtitle2">
                    Convenience charges
                  </Typography>
                  <Typography variant="subtitle2" sx={{ ml: 48 }}>
                    ₹{invoice[0].conveniance_charges}
                  </Typography>
                </div>
              </>
            ) : null}

            {/* {invoice[0]?.discount_type === 1 ||
              invoice[0]?.discount_type === 2 ? (
              <>
                <hr />
                <div
                  style={{
                    display: "flex",
                    marginLeft: "16px",
                    marginTop: "10px",
                  }}
                >
                  <Typography variant="subtitle2">
                    Discount Value ({invoice[0].discount_type === 1 ? "%" : "₹"}
                    )
                  </Typography>
                  <Typography variant="subtitle2" sx={{ ml: 50 }}>
                    {invoice[0].discount_value}
                  </Typography>
                </div>
              </>
            ) : null} */}

            {invoice[0]?.discount_type === 1 ||
              invoice[0]?.discount_type === 2 ? (
              <>
                <hr />

                <div
                  style={{
                    marginLeft: "16px",
                    marginTop: "10px",
                    width: "400px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 600 }}
                    >
                      Discount Value (
                      {invoice[0]?.discount_type === 1 ? "%" : "₹"})
                    </Typography>

                    <Typography variant="subtitle2">
                      {invoice[0]?.discount_value || 0}
                    </Typography>
                  </div>
                </div>
              </>
            ) : null}

            <hr />

            <div
              style={{
                marginLeft: "16px",
                marginTop: "10px",
                width: "400px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Total Amount (INR)
                </Typography>

                <Typography variant="subtitle2">
                  ₹{invoice[0]?.Total_amount || 0}
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Final Amount (INR)
                </Typography>

                <Typography variant="subtitle2">
                  ₹{invoice[0]?.Final_amount || 0}
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Received Amount (INR)
                </Typography>

                <Typography variant="subtitle2">
                  ₹{invoice[0]?.amount_paid || 0}
                </Typography>
              </div>
            </div>

            {/* <div
              style={{ display: "flex", marginLeft: "16px", marginTop: "10px" }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Total Amount (INR)
              </Typography>
              <Typography variant="subtitle2" sx={{ ml: 50 }}>
                ₹{invoice[0]?.Total_amount || 0}
              </Typography>
            </div>
            <div
              style={{ display: "flex", marginLeft: "16px", marginTop: "10px" }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Final Amount (INR)
              </Typography>
              <Typography variant="subtitle2" sx={{ ml: 50.5 }}>
                ₹{invoice[0]?.Final_amount || 0}
              </Typography>
            </div>
            <div
              style={{ display: "flex", marginLeft: "16px", marginTop: "10px" }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Received Amount (INR)
              </Typography>
              <Typography variant="subtitle2" sx={{ ml: 47 }}>
                ₹{invoice[0]?.amount_paid || 0}
              </Typography>
            </div> */}
          </div>

          <div style={{ marginTop: "40px", marginLeft: "10px" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }} gutterBottom>
              Declaration:
            </Typography>
            <Typography variant="body2">
              We declare that this Bill shows the actual price of the services
              described and that all particulars are true and correct.
            </Typography>
          </div>

          <Box
            style={{
              width: "110%",
              background: "#15CEC9",
              marginLeft: "-32px",
              marginTop: "10px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Company's Bank Detail:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  rowGap: 1,
                }}
              >
                <Typography variant="subtitle2">Bank Name</Typography>
                <Typography variant="body2">HDFC BANK</Typography>
                <Typography variant="subtitle2">IFSC Code</Typography>
                <Typography variant="body2">HDFC00000007</Typography>
                <Typography variant="subtitle2">Branch</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  Laukik Apartment,{"\n"}Bhandarkar Road, Pune
                </Typography>
                <Typography variant="subtitle2">Account</Typography>
                <Typography variant="body2">50200109761561</Typography>
              </Box>
            </CardContent>
            <CardContent sx={{ mt: 5 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PhoneOutlinedIcon sx={{ color: "#4A4A4A" }} />
                  <Typography variant="body2">7620400100</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmailOutlinedIcon sx={{ color: "#4A4A4A" }} />
                  <Typography variant="body2">
                    helpdesk@sperohomehealth.in
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LanguageOutlinedIcon sx={{ color: "#4A4A4A" }} />
                  <Typography variant="body2">sperohomehealth.in</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Box>
          <hr
            style={{
              width: "109.8%",
              height: "5px",
              background: "#FAAF30",
              marginLeft: "-32px",
              marginTop: "-5px",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "-20px",
          }}
        >
          <Tooltip title="Send Mail" arrow>
            <span>
              <IconButton
                onClick={sendProformaMail}
                disabled={disabled || loading}
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: "#B50001",
                  border: "1px solid #B50001",
                  "&:hover": { backgroundColor: "#FFFFFF" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                  mr: 2,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.7 : 1,
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#B50001" }} />
                ) : (
                  <>
                    <MailOutlineIcon fontSize="small" />
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Send Mail
                    </Typography>
                  </>
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleDownloadPDF}
            sx={{
              background: "#B50001",
              "&:hover": { backgroundColor: "#B50001", cursor: "pointer" },
            }}
          >
            <PictureAsPdfOutlinedIcon />
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ color: "#FFFFFF", ml: 1 }}
          >
            <CloseIcon />
          </Button>
        </div>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PackageInvoice;
